import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/page-header";
import { FilterSidebar, type FilterGroup } from "@/components/filter-sidebar";
import { ListToolbar } from "@/components/list-toolbar";
import { PaginationBar } from "@/components/pagination-bar";
import { BulkActionMenu, type BulkActionItem } from "@/components/bulk-action-menu";
import { BulkModal } from "@/components/bulk-modal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useList } from "@/hooks/use-list";
import { useSelection } from "@/hooks/use-selection";
import { exportCSV } from "@/lib/csv-export";
import { api } from "@/lib/api";
import { Plus, Trash2, Upload, Mail, Linkedin, Phone, PhoneCall, MoreHorizontal, UserCheck, Edit3, Download, Send } from "lucide-react";
import { cn } from "@/lib/cn";

type Filter = "all" | "my" | "recent7" | "recent30" | "optedOut" | "optedIn" | "hasEmail" | "hasLinkedin" | "noAccount";
type BulkMode = null | "delete" | "transfer" | "update";

export default function ContactsPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [filter, setFilter] = useState<Filter>("all");
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(true);

  const sel = useSelection();

  const extraParams = useMemo(() => {
    const p: Record<string, unknown> = {};
    if (filter === "optedOut") p.optedOut = true;
    if (filter === "optedIn")  p.optedOut = false;
    if (ownerId)               p.ownerId = ownerId;
    return p;
  }, [filter, ownerId]);

  const { data, isLoading } = useList<any>("/contacts", { q, page, pageSize, sortBy, sortDir, ...extraParams });

  const rows = useMemo(() => {
    const all = data?.data ?? [];
    const since = (days: number) => Date.now() - days * 86400_000;
    switch (filter) {
      case "recent7":     return all.filter((c: any) => new Date(c.createdAt).getTime() >= since(7));
      case "recent30":    return all.filter((c: any) => new Date(c.createdAt).getTime() >= since(30));
      case "hasEmail":    return all.filter((c: any) => !!c.email);
      case "hasLinkedin": return all.filter((c: any) => !!c.linkedinUrl);
      case "noAccount":   return all.filter((c: any) => !c.accountId);
      default:            return all;
    }
  }, [data, filter]);
  const total = data?.pagination?.total;
  const visibleIds = rows.map((r: any) => r.id);

  useEffect(() => { sel.clear(); /* eslint-disable-next-line */ }, [filter, q, page, ownerId]);

  const users = useQuery({
    queryKey: ["/users"],
    queryFn: async () => (await api.get("/users")).data.data as { id: string; name: string }[],
  });

  const groups: FilterGroup[] = [
    {
      title: "Standard",
      items: [
        { label: "All contacts",    active: filter === "all",       onClick: () => setFilter("all"),       count: total },
        { label: "My contacts",     active: filter === "my",        onClick: () => setFilter("my") },
        { label: "Created last 7d", active: filter === "recent7",   onClick: () => setFilter("recent7") },
        { label: "Created last 30d",active: filter === "recent30",  onClick: () => setFilter("recent30") },
      ],
    },
    {
      title: "Engagement",
      items: [
        { label: "Has email",       active: filter === "hasEmail",    onClick: () => setFilter("hasEmail") },
        { label: "Has LinkedIn",    active: filter === "hasLinkedin", onClick: () => setFilter("hasLinkedin") },
        { label: "No account",      active: filter === "noAccount",   onClick: () => setFilter("noAccount") },
      ],
    },
    {
      title: "Compliance",
      items: [
        { label: "Subscribed",      active: filter === "optedIn",     onClick: () => setFilter("optedIn") },
        { label: "Unsubscribed",    active: filter === "optedOut",    onClick: () => setFilter("optedOut") },
      ],
    },
    {
      title: "Owner",
      items: [
        { label: "All owners", active: !ownerId, onClick: () => setOwnerId(null) },
        ...(users.data ?? []).map((u) => ({ label: u.name, active: ownerId === u.id, onClick: () => setOwnerId(u.id) })),
      ],
    },
  ];

  async function del(id: string, name: string) {
    if (!confirm(`Delete contact "${name}"?`)) return;
    await api.delete(`/contacts/${id}`);
    qc.invalidateQueries({ queryKey: ["/contacts"] });
  }

  // --- Bulk operations ---
  const [bulkMode, setBulkMode] = useState<BulkMode>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [transferOwnerId, setTransferOwnerId] = useState("");
  const [updateField, setUpdateField] = useState<"leadSource" | "contactGeography" | "communicationPref" | "optedOut" | "solutions" | "cbcOfferings">("leadSource");
  const [updateValue, setUpdateValue] = useState("");

  const bulkMut = useMutation({
    mutationFn: async () => {
      const ids = sel.ids;
      if (bulkMode === "delete")      await api.post("/contacts/bulk-delete",   { ids });
      if (bulkMode === "transfer")    await api.post("/contacts/bulk-transfer", { ids, ownerId: transferOwnerId });
      if (bulkMode === "update") {
        let value: any = updateValue;
        if (updateField === "optedOut") value = updateValue === "true";
        await api.post("/contacts/bulk-update", { ids, field: updateField, value });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/contacts"] });
      sel.clear();
      setBulkMode(null);
      setBulkError(null);
    },
    onError: (err: any) => setBulkError(err?.response?.data?.message ?? "Action failed."),
  });

  function doExport() {
    const selected = sel.count > 0 ? rows.filter((r: any) => sel.isSelected(r.id)) : rows;
    exportCSV(`contacts-${new Date().toISOString().slice(0, 10)}`, selected, [
      { key: "name",       label: "Name" },
      { key: "firstName",  label: "First Name" },
      { key: "lastName",   label: "Last Name" },
      { key: "title",      label: "Title" },
      { key: "email",      label: "Email" },
      { key: "phone",      label: "Phone" },
      { key: "mobile",     label: "Mobile" },
      { key: "linkedinUrl",      label: "LinkedIn" },
      { key: "accountName",      label: "Account", get: (r: any) => r.account?.name },
      { key: "leadSource",       label: "Lead Source" },
      { key: "contactGeography", label: "Geography" },
      { key: "createdAt",        label: "Created" },
    ]);
  }

  function massEmail() {
    const selected = rows.filter((r: any) => sel.isSelected(r.id));
    const emails = selected.map((r: any) => r.email).filter(Boolean);
    if (emails.length === 0) { alert("No selected contact has an email."); return; }
    window.open(`mailto:?bcc=${encodeURIComponent(emails.join(","))}`, "_blank");
  }

  async function addToQueue() {
    const ids = sel.ids;
    if (ids.length === 0) return;
    if (!confirm(`Add ${ids.length} contact${ids.length > 1 ? "s" : ""} to your call queue? They'll be reassigned to you.`)) return;
    await api.post("/call-queue/add", { kind: "contact", ids });
    qc.invalidateQueries({ queryKey: ["/contacts"] });
    qc.invalidateQueries({ queryKey: ["/call-queue"] });
    sel.clear();
  }

  const bulkItems: BulkActionItem[] = [
    { label: "Add to Call Queue", icon: <PhoneCall className="h-4 w-4"/>, requiresSelection: 1, onClick: addToQueue },
    { label: "Mass Transfer", icon: <UserCheck className="h-4 w-4"/>, requiresSelection: 1, onClick: () => setBulkMode("transfer") },
    { label: "Mass Update",   icon: <Edit3 className="h-4 w-4"/>,     requiresSelection: 1, onClick: () => setBulkMode("update") },
    { label: "Mass Email",    icon: <Send className="h-4 w-4"/>,      requiresSelection: 1, onClick: massEmail },
    { label: "Mass Delete",   icon: <Trash2 className="h-4 w-4"/>,    requiresSelection: 1, onClick: () => setBulkMode("delete"), danger: true },
    { label: "Export to CSV", icon: <Download className="h-4 w-4"/>,  onClick: doExport, divideAbove: true },
  ];

  const updateOptions: Record<string, string[]> = {
    leadSource: ["Apollo", "LinkedIn", "Webinar", "Event", "Partner", "Website", "Referral", "Cold Call"],
    contactGeography: ["NA", "EMEA", "APAC", "LATAM", "India"],
    communicationPref: ["EMAIL", "PHONE", "LINKEDIN", "SMS", "NO_CONTACT"],
    optedOut: ["true", "false"],
    solutions: [], cbcOfferings: [],
  };

  return (
    <>
      <PageHeader
        title="Contacts"
        description="People across all accounts."
        actions={
          <>
            <Button asChild variant="outline"><Link to="/contacts/import"><Upload className="mr-1 h-4 w-4"/>Import CSV</Link></Button>
            <Button asChild><Link to="/contacts/new"><Plus className="mr-1 h-4 w-4"/>Create Contact</Link></Button>
            <BulkActionMenu selectedCount={sel.count} items={bulkItems} />
          </>
        }
      />

      <ListToolbar
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((v) => !v)}
        sortBy={sortBy}
        sortDir={sortDir}
        sortOptions={[
          { value: "createdAt", label: "Created" },
          { value: "updatedAt", label: "Last modified" },
          { value: "name",      label: "Name" },
          { value: "email",     label: "Email" },
        ]}
        onSortChange={(by, dir) => { setSortBy(by); setSortDir(dir); }}
        rightSlot={<Input placeholder="Search contacts…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="h-8 w-64" />}
      />

      <div className="flex flex-1 min-h-[60vh]">
        {filtersOpen && <FilterSidebar groups={groups} onClear={() => setFilter("all")} />}

        <div className="flex flex-1 flex-col min-w-0">
          <div className="flex-1 overflow-auto">
            <Table>
              <Thead>
                <Tr>
                  <Th className="w-10">
                    <input
                      type="checkbox"
                      checked={sel.allSelected(visibleIds)}
                      onChange={() => sel.toggleAll(visibleIds)}
                    />
                  </Th>
                  <Th>Contact Name</Th>
                  <Th>Title</Th>
                  <Th>Account</Th>
                  <Th>Email</Th>
                  <Th>Phone / Mobile</Th>
                  <Th>LinkedIn</Th>
                  <Th>Owner</Th>
                  <Th className="w-10"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {isLoading && <Tr><Td colSpan={9} className="py-8 text-center text-muted-foreground">Loading…</Td></Tr>}
                {!isLoading && rows.length === 0 && (
                  <Tr><Td colSpan={9} className="py-12 text-center text-muted-foreground">No contacts match this view.</Td></Tr>
                )}
                {rows.map((c: any) => (
                  <Tr key={c.id} className="group">
                    <Td>
                      <input type="checkbox" checked={sel.isSelected(c.id)} onChange={() => sel.toggle(c.id)} />
                    </Td>
                    <Td>
                      <Link to={`/contacts/${c.id}`} className="font-medium hover:underline">
                        {c.salutation ? `${c.salutation} ` : ""}{c.name}
                      </Link>
                      {c.optedOut && <Badge variant="secondary" className="ml-2">Opted out</Badge>}
                    </Td>
                    <Td className="text-muted-foreground">{c.title ?? "—"}</Td>
                    <Td>{c.account?.name ? (
                      <Link to={`/accounts/${c.account.id}`} className="hover:underline">{c.account.name}</Link>
                    ) : <span className="text-muted-foreground">—</span>}</Td>
                    <Td>{c.email ? (
                      <a href={`mailto:${c.email}`} className="hover:underline text-muted-foreground">{c.email}</a>
                    ) : <span className="text-muted-foreground">—</span>}</Td>
                    <Td className="text-muted-foreground">{c.mobile ?? c.phone ?? "—"}</Td>
                    <Td>{c.linkedinUrl
                      ? <a href={c.linkedinUrl} target="_blank" rel="noreferrer" className="text-muted-foreground hover:underline">Profile</a>
                      : <span className="text-muted-foreground">—</span>}</Td>
                    <Td className="text-muted-foreground">{c.owner?.name ?? "—"}</Td>
                    <Td>
                      <div className={cn("flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity")}>
                        {c.email && (
                          <a href={`mailto:${c.email}`} title="Email" className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                            <Mail className="h-3.5 w-3.5"/>
                          </a>
                        )}
                        {(c.mobile || c.phone) && (
                          <a href={`tel:${c.mobile ?? c.phone}`} title="Call" className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                            <Phone className="h-3.5 w-3.5"/>
                          </a>
                        )}
                        {c.linkedinUrl && (
                          <a href={c.linkedinUrl} target="_blank" rel="noreferrer" title="LinkedIn" className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                            <Linkedin className="h-3.5 w-3.5"/>
                          </a>
                        )}
                        <button onClick={() => del(c.id, c.name)} title="Delete" className="flex h-7 w-7 items-center justify-center rounded-md text-destructive hover:bg-muted">
                          <Trash2 className="h-3.5 w-3.5"/>
                        </button>
                        <Link to={`/contacts/${c.id}`} title="Open" className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                          <MoreHorizontal className="h-3.5 w-3.5"/>
                        </Link>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>

          <PaginationBar
            page={page} pageSize={pageSize} total={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          />
        </div>
      </div>

      <BulkModal
        open={bulkMode === "delete"}
        onClose={() => setBulkMode(null)}
        title="Mass Delete Contacts"
        description={`Delete ${sel.count} selected contacts? This cannot be undone.`}
        confirmLabel={`Delete ${sel.count}`}
        confirmDanger
        busy={bulkMut.isPending}
        onConfirm={() => bulkMut.mutate()}
        error={bulkError}
      >
        <div />
      </BulkModal>

      <BulkModal
        open={bulkMode === "transfer"}
        onClose={() => setBulkMode(null)}
        title="Mass Transfer"
        description={`Reassign ${sel.count} contacts to a new owner.`}
        confirmLabel={`Transfer ${sel.count}`}
        confirmDisabled={!transferOwnerId}
        busy={bulkMut.isPending}
        onConfirm={() => bulkMut.mutate()}
        error={bulkError}
      >
        <Label htmlFor="owner">New owner</Label>
        <Select id="owner" value={transferOwnerId} onChange={(e) => setTransferOwnerId(e.target.value)}>
          <option value="">— Select user —</option>
          {users.data?.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </Select>
      </BulkModal>

      <BulkModal
        open={bulkMode === "update"}
        onClose={() => setBulkMode(null)}
        title="Mass Update"
        description={`Update a single field on ${sel.count} contacts.`}
        confirmLabel={`Update ${sel.count}`}
        confirmDisabled={!updateValue}
        busy={bulkMut.isPending}
        onConfirm={() => bulkMut.mutate()}
        error={bulkError}
      >
        <Label htmlFor="upd-field">Field</Label>
        <Select id="upd-field" value={updateField} onChange={(e) => setUpdateField(e.target.value as any)}>
          <option value="leadSource">Lead source</option>
          <option value="contactGeography">Geography</option>
          <option value="communicationPref">Preferred channel</option>
          <option value="solutions">Solutions</option>
          <option value="cbcOfferings">CBC offerings</option>
          <option value="optedOut">Opted out (true/false)</option>
        </Select>
        <Label htmlFor="upd-val" className="mt-3">New value</Label>
        {updateOptions[updateField]?.length ? (
          <Select id="upd-val" value={updateValue} onChange={(e) => setUpdateValue(e.target.value)}>
            <option value="">— Select value —</option>
            {updateOptions[updateField].map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        ) : (
          <Input id="upd-val" value={updateValue} onChange={(e) => setUpdateValue(e.target.value)} />
        )}
      </BulkModal>
    </>
  );
}
